import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api, buildQueryString } from '@/lib/api'

export type RegulatoryDeadlineKind =
  | 'deadline'
  | 'amendment'
  | 'transition'
  | 'reporting'
  | 'consultation_close'

export type RegulatoryDeadlineStatus = 'upcoming' | 'passed' | 'cancelled'

export type AdminRegulatoryDeadline = {
  id: string
  framework_id: string
  kind: RegulatoryDeadlineKind
  due_date: string
  event_key: string
  label: string
  description: string | null
  source_url: string
  status: RegulatoryDeadlineStatus
  version: number
  scraped_at: string
  superseded_by_id: string | null
  created_at: string
  updated_at: string
  // Present on the admin list endpoint (joined from frameworks).
  framework_name?: string
  framework_slug?: string
  framework_jurisdiction?: string
}

export type CreateRegulatoryDeadlineInput = {
  framework_id: string
  kind: RegulatoryDeadlineKind
  due_date: string
  label: string
  description?: string | null
  source_url: string
}

// KAN-275 (E2) — `label` and `due_date` are IMMUTABLE identity fields; only
// these non-identity fields are editable. To change identity, cancel + recreate.
// `expected_version` is mandatory for strict optimistic concurrency control.
export type UpdateRegulatoryDeadlineInput = {
  description?: string | null
  source_url?: string
  status?: RegulatoryDeadlineStatus
  expected_version: number
}

export type AdminRegulatoryDeadlinesResponse = {
  deadlines: AdminRegulatoryDeadline[]
  total: number
  limit: number
  offset: number
}

export type AdminRegulatoryDeadlinesQuery = {
  status?: RegulatoryDeadlineStatus
  framework_id?: string
  limit?: number
  offset?: number
}

export const ADMIN_REGULATORY_DEADLINES_QUERY_KEY = ['admin-regulatory-deadlines'] as const

export function useAdminRegulatoryDeadlines(params: AdminRegulatoryDeadlinesQuery = {}) {
  return useQuery<AdminRegulatoryDeadlinesResponse>({
    queryKey: [...ADMIN_REGULATORY_DEADLINES_QUERY_KEY, params] as const,
    queryFn: () =>
      api.get<AdminRegulatoryDeadlinesResponse>(
        `/admin/regulatory/deadlines${buildQueryString({
          status: params.status,
          framework_id: params.framework_id,
          limit: params.limit,
          offset: params.offset,
        })}`,
      ),
    staleTime: 10_000,
  })
}

export function useCreateRegulatoryDeadline() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateRegulatoryDeadlineInput) =>
      api.post<AdminRegulatoryDeadline>('/admin/regulatory/deadlines', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_REGULATORY_DEADLINES_QUERY_KEY })
    },
  })
}

export function useUpdateRegulatoryDeadline() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateRegulatoryDeadlineInput }) =>
      api.patch<AdminRegulatoryDeadline>(`/admin/regulatory/deadlines/${id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_REGULATORY_DEADLINES_QUERY_KEY })
    },
  })
}

// KAN-421 — cancel is a versioned write, not a plain delete. It carries the
// same mandatory `expected_version` lock token as the edit path so a stale
// cancel cannot overwrite a newer edit; the API answers 409 on mismatch.
// The token travels on the query string because DELETE request bodies have no
// defined semantics and may be dropped in transit (see the API route comment).
export type CancelRegulatoryDeadlineInput = {
  id: string
  expected_version: number
}

export function useCancelRegulatoryDeadline() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, expected_version }: CancelRegulatoryDeadlineInput) =>
      api.delete<AdminRegulatoryDeadline>(
        `/admin/regulatory/deadlines/${id}${buildQueryString({ expected_version })}`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_REGULATORY_DEADLINES_QUERY_KEY })
    },
    // KAN-421 — a 409 means this row's `version` token is dead: the server state
    // moved under us, so every retry with the same token 409s forever. Refetch
    // so the row re-renders carrying the CURRENT version and the operator's next
    // click can succeed. This does NOT swallow the error — react-query still
    // rejects the mutation promise, so the call site surfaces the notice too.
    //
    // Scoped to 409 deliberately: only a conflict proves the cache is stale.
    // Other failures (5xx, network) carry no such proof, and invalidating on
    // them would fire extra GETs at an already-failing API.
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        queryClient.invalidateQueries({ queryKey: ADMIN_REGULATORY_DEADLINES_QUERY_KEY })
      }
    },
  })
}
